// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { WARHOL_DOCUMENT_EXTENSIONS } from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowUp,
  Camera,
  CircleNotch,
  Paperclip,
  Plus,
} from "@phosphor-icons/react";
import {
  Button,
  Flex,
  IconButton,
  Popover,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { type ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2TransportApi } from "./useAskV2Transport";

import { useAttachedFiles } from "../../hooks/useAttachedFiles";
import {
  AskV2ComposerModeToggle,
  type AskV2ComposerMode,
} from "../home/ComposerModeToggle";
import { type AskV2Attachment } from "../services/chat";

const WARHOL_ACCEPT = Array.from(WARHOL_DOCUMENT_EXTENSIONS)
  .sort()
  .map((ext) => `.${ext}`);

type SummonBarProps = {
  transport: AskV2TransportApi;
  landing?: boolean;
  /** When true, block send until the route has bound a chat id (project feed). */
  requireBoundChatId?: boolean;
  composerMode?: AskV2ComposerMode;
  onComposerModeChange?: (mode: AskV2ComposerMode) => void;
  needsProjectSelection?: boolean;
  projectSubmitting?: boolean;
  onProjectSubmit?: (input: {
    message: string;
    attachments: AskV2Attachment[];
  }) => void | boolean | Promise<void | boolean>;
};

export function AskV2SummonBar({
  transport,
  landing = false,
  requireBoundChatId = false,
  composerMode: composerModeProp,
  onComposerModeChange,
  needsProjectSelection = false,
  projectSubmitting = false,
  onProjectSubmit,
}: SummonBarProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const [composerModeLocal, setComposerModeLocal] =
    useState<AskV2ComposerMode>("chat");
  const composerMode = composerModeProp ?? composerModeLocal;
  const setComposerMode = onComposerModeChange ?? setComposerModeLocal;
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles();
  const hasReadyAttachments = readyAttachments.length > 0;
  const hasQuotedContexts = transport.quotedContexts.length > 0;
  const canSend =
    (Boolean(transport.composer.trim()) ||
      hasReadyAttachments ||
      hasQuotedContexts) &&
    !hasPending &&
    !transport.sending &&
    !transport.inFlight &&
    !projectSubmitting &&
    !needsProjectSelection &&
    (!requireBoundChatId || Boolean(transport.chatId));
  const placeholder = landing
    ? composerMode === "project"
      ? t("home.askInProject")
      : t("askAnything")
    : hasQuotedContexts
      ? t("askV2.transport.replyPlaceholder")
      : t("askV2.transport.placeholder");

  const submit = () => {
    if (!canSend) return;
    const attachments = [...readyAttachments];
    const message = transport.composer;
    if (landing && composerMode === "project" && onProjectSubmit) {
      // why: mirror the chat path's composer reset, but only after a successful
      // post — a failed project post must keep both text and its attachments so
      // the user can retry. Clearing eagerly would drop already-uploaded files.
      void Promise.resolve(onProjectSubmit({ message, attachments })).then(
        (posted) => {
          if (posted) clearAll();
        },
      );
      return;
    }
    clearAll();
    void transport.send({ attachments });
  };

  return (
    <form
      data-testid="ask-v2-summon-bar"
      className="flex w-full flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div
        className={
          landing
            ? "relative z-20 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-lg transition-all duration-200 focus-within:border-puntt-accent-7"
            : "relative z-20 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20"
        }
        data-testid={landing ? "ask-v2-home-composer" : "ask-v2-composer-card"}
      >
        {hasQuotedContexts ? (
          <Flex
            gap="2"
            wrap="wrap"
            className="px-3 pt-2"
            data-testid="ask-v2-quoted-contexts"
          >
            {transport.quotedContexts.map((ctx) => (
              <Flex
                key={ctx.id}
                align="center"
                gap="1"
                className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-v2-quoted-context-${ctx.id}`}
              >
                <Text
                  size="1"
                  className="max-w-[200px] truncate text-puntt-neutral-gray-11"
                >
                  {ctx.text}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-v2-remove-quoted-context-${ctx.id}`}
                  onClick={() => transport.removeQuotedContext(ctx.id)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  aria-label={t("askV2.transport.removeQuotedContext")}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        ) : null}
        <TextArea
          data-testid="ask-v2-composer"
          size="3"
          variant="surface"
          placeholder={placeholder}
          value={transport.composer}
          disabled={
            transport.sending || transport.inFlight || projectSubmitting
          }
          onChange={(event) => transport.setComposer(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className="w-full resize-none"
          style={{ height: landing ? "88px" : "72px" }}
        />
        {attachedItems.length > 0 && (
          <Flex
            gap="2"
            wrap="wrap"
            className="px-3 pb-1"
            data-testid="ask-v2-attached-files"
          >
            {attachedItems.map((item, idx) => (
              <Flex
                key={item.id}
                align="center"
                gap="1"
                className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-v2-attached-file-${item.id}`}
              >
                {item.status === "pending" && (
                  <CircleNotch
                    size={12}
                    weight="bold"
                    className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                  />
                )}
                <Text
                  size="1"
                  className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                >
                  {item.file.name}
                  {item.status === "error" && " (failed)"}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-v2-remove-attachment-${item.id}`}
                  onClick={() => removeFile(idx)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  aria-label={t("home.removeFileAriaLabel", {
                    name: item.file.name,
                  })}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        )}
        <Flex justify="between" align="center" className="p-1">
          <Flex align="center" gap="2">
            <AskV2AttachMenu
              disabled={transport.sending}
              onFilesSelected={addFiles}
              onScreenshotCaptured={addFile}
            />
            {landing ? (
              <AskV2ComposerModeToggle
                data-testid="ask-v2-composer-mode"
                value={composerMode}
                onChange={setComposerMode}
              />
            ) : null}
          </Flex>
          {projectSubmitting ? (
            <Button
              type="button"
              size="2"
              className="cursor-default rounded-xl"
              data-testid="ask-v2-project-submitting"
              disabled
            >
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            </Button>
          ) : transport.inFlight || transport.sending ? (
            <Button
              type="button"
              color="red"
              size="2"
              className="cursor-pointer rounded-xl"
              data-testid="ask-v2-stop"
              onClick={() => {
                void transport.abort();
              }}
              disabled={transport.aborting}
            >
              {t("askV2.transport.stop")}
            </Button>
          ) : (
            <Button
              type="submit"
              size="2"
              className="cursor-pointer rounded-xl"
              data-testid="ask-v2-ask"
              disabled={!canSend}
              aria-label={tCommon("nav.askPuntt")}
            >
              <ArrowUp size={14} weight="bold" />
              {tCommon("nav.askPuntt")}
            </Button>
          )}
        </Flex>
      </div>
    </form>
  );
}

function AskV2AttachMenu({
  disabled,
  onFilesSelected,
  onScreenshotCaptured,
}: {
  disabled: boolean;
  onFilesSelected: (files: File[]) => void;
  onScreenshotCaptured: (file: File) => void;
}) {
  const { t } = useTranslation("ui");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFilesSelected(Array.from(files));
      setMenuOpen(false);
    }
    event.target.value = "";
  };

  const handleScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" } as MediaTrackConstraints,
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d")?.drawImage(video, 0, 0);
      stream.getTracks().forEach((track) => track.stop());

      canvas.toBlob((blob) => {
        if (!blob) return;
        onScreenshotCaptured(
          new File([blob], `screenshot-${Date.now()}.png`, {
            type: "image/png",
          }),
        );
        setMenuOpen(false);
      }, "image/png");
    } catch {
      // User cancelled the display picker, or the browser refused it.
    }
  };

  return (
    <Popover.Root
      open={menuOpen}
      onOpenChange={(next) => {
        if (disabled) return;
        setMenuOpen(next);
      }}
    >
      <Popover.Trigger>
        <IconButton
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          disabled={disabled}
          className="!m-0 cursor-pointer rounded-xl"
          data-testid="ask-v2-attach-btn"
          aria-label={t("addAttachmentOrContext")}
        >
          <Plus size={16} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        side="top"
        align="start"
        className="max-h-[min(70vh,420px)] w-[260px] overflow-y-auto p-1"
      >
        <input
          ref={fileInputRef}
          type="file"
          aria-label={t("addFilesOrPhotos")}
          data-testid="ask-v2-attach-file-input"
          // why: Tailwind `hidden` is display:none; some browsers refuse to open a picker from that
          className="sr-only"
          accept={WARHOL_ACCEPT.join(",")}
          onChange={handleFileSelect}
          multiple
        />
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          className="w-full cursor-pointer justify-start py-2 pl-5"
          onClick={() => fileInputRef.current?.click()}
          data-testid="ask-v2-add-files-btn"
        >
          <Paperclip size={16} />
          {t("addFilesOrPhotos")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          className="w-full cursor-pointer justify-start py-2 pl-5"
          onClick={() => {
            void handleScreenshot();
          }}
          data-testid="ask-v2-screenshot-btn"
        >
          <Camera size={16} />
          {t("takeScreenshot")}
        </Button>
      </Popover.Content>
    </Popover.Root>
  );
}
